import { useState, useEffect } from "react";
import { GamesItem } from "./GamesItem";
import { gamesAPI, Game } from "./api";
import { LoadingSpinner } from "./LoadingSpinner";

type FilterOption = 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED' | 'ALL';

function classNames(...classes: any) {
	return classes.filter(Boolean).join(" ");
}

export const GamesList = () => {
	const [filterOption, setFilterOption] = useState<FilterOption>('IN_PROGRESS');
	const [games, setGames] = useState<Game[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchGames = async () => {
			try {
				setIsLoading(true);
				setError(null);

				// Fetch all games and filter client-side for IN_PROGRESS
				const fetchedGames = await gamesAPI.getGames();

				// Apply filter
				let filteredGames = fetchedGames;
				if (filterOption === 'IN_PROGRESS') {
					filteredGames = fetchedGames.filter(
						game => game.status === 'PENDING' || game.status === 'ACTIVE'
					);
				} else if (filterOption !== 'ALL') {
					filteredGames = fetchedGames.filter(game => game.status === filterOption);
				}

				setGames(filteredGames);
			} catch (err) {
				console.error('Failed to fetch games:', err);
				setError('Failed to load games. Please try again.');
			} finally {
				setIsLoading(false);
			}
		};

		fetchGames();
	}, [filterOption]);

	return (
		<>
			<div className="flex w-full justify-center">
				<span className="isolate inline-flex rounded-md shadow-sm">
					<button
						type="button"
						className={classNames(
							filterOption === 'IN_PROGRESS'
								? "bg-orange-600 text-white"
								: "bg-white hover:bg-orange-50",
							"relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
						)}
						onClick={() => setFilterOption('IN_PROGRESS')}
					>
						In Progress
					</button>
					<button
						type="button"
						className={classNames(
							filterOption === 'COMPLETED'
								? "bg-orange-600 text-white"
								: "bg-white hover:bg-orange-50",
							"relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
						)}
						onClick={() => setFilterOption('COMPLETED')}
					>
						Completed
					</button>
					<button
						type="button"
						className={classNames(
							filterOption === 'ABANDONED'
								? "bg-orange-600 text-white"
								: "bg-white hover:bg-orange-50",
							"relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
						)}
						onClick={() => setFilterOption('ABANDONED')}
					>
						Abandoned
					</button>
					<button
						type="button"
						className={classNames(
							filterOption === 'ALL'
								? "bg-orange-600 text-white"
								: "bg-white hover:bg-orange-50",
							"relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10"
						)}
						onClick={() => setFilterOption('ALL')}
					>
						All
					</button>
				</span>
			</div>

			{isLoading ? (
				<div className="flex justify-center items-center py-12">
					<LoadingSpinner />
				</div>
			) : error ? (
				<div className="text-center py-12">
					<p className="text-red-600">{error}</p>
				</div>
			) : games.length === 0 ? (
				<div className="text-center py-12">
					<p className="text-gray-500">No games found. Create a new game to get started!</p>
				</div>
			) : (
				<ul className="divide-y divide-gray-200">
					{games.map((game) => (
						<li key={game.id} className="py-4">
							<GamesItem game={game} />
						</li>
					))}
				</ul>
			)}
		</>
	);
};
